if (!customElements.get('wlsn-buybox-sticky')) {
  class WLSNStickyBuybox extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        isOpen: 'is-open',
        buybox: 'wlsn-buybox',
        footer: 'wlsn-footer',
        frequencyForm: 'wlsn-product-form-frequency',
        inputPurchaseTypeChecked: 'input[name="purchase-type"]:checked',
        selectorButton: 'js-sticky-selector-button',
        selectorLabel: 'js-sticky-selector-label',
        popup: 'js-sticky-popup',
        quantityInput: 'js-quantity-input',
        quantityLabel: 'js-quantity-label',
        quantityItem: 'js-quantity-item',
        planNameSingle: 'js-plan-name-single',
        otpLabel: 'js-otp-label',
      };

      // Selectors for elements that hide the sticky when they are visible in the viewport
      this.hideConditionSelectors = [
        { selector: this.classes.buybox, description: 'Buybox' },
        { selector: this.classes.footer, description: 'Footer' },
      ];

      this.observers = {};
      this.visibilityStates = {};

      // Only cache elements that live inside `this` — they are stable and never re-rendered.
      // External elements (buybox, frequencyForm) are queried fresh each time via getters
      // because Shopify AJAX section re-renders replace them in the DOM.
      this._internalElementsCache = null;

      this.isSyncing = false;

      this.purchaseType = {
        OTP: 'one-time',
        SUB: 'subscription',
      };

      // Tracks the label parts for the selector button.
      // Updated by each event handler — never read from the DOM at render time.
      this.currentState = {
        quantityLabel: '',   // text from quantity selector (e.g. "3-Pack")
        frequencyLabel: '',  // plan name for subscription, OTP label text for one-time
      };

      // Bound methods
      this.boundHandleResize = this._handleResize.bind(this);
      this.boundHandleDocumentClick = this._handleDocumentClick.bind(this);
      this.boundHandleSelectorButtonClick = this._handleSelectorButtonClick.bind(this);
      this.boundHandlePopupChange = this._handlePopupChange.bind(this);
      this.boundHandlePurchaseTypeChange = this._handlePurchaseTypeChange.bind(this);
      this.boundHandleProductVariantChange = this._handleProductVariantChange.bind(this);
      this.boundHandlePageUpdated = this._handlePageUpdated.bind(this);
      this.boundHandleSellingPlanChange = this._handleSellingPlanChange.bind(this);
      this.boundHandleFrequencyPriceUpdate = this._handleFrequencyPriceUpdate.bind(this);
    }

    /**
     * Cached references to elements inside `this` (stable, never replaced by AJAX).
     * @returns {object}
     */
    get elements() {
      if (!this._internalElementsCache) {
        this._internalElementsCache = {
          selectorButton: this.querySelector(`.${this.classes.selectorButton}`),
          selectorLabel: this.querySelector(`.${this.classes.selectorLabel}`),
          popup: this.querySelector(`.${this.classes.popup}`),
        };
      }
      return this._internalElementsCache;
    }

    /**
     * Main buybox element — queried fresh every time.
     * Shopify AJAX section re-renders replace this node, so it must never be cached.
     * @returns {HTMLElement|null}
     */
    get buybox() {
      return document.querySelector(this.classes.buybox);
    }

    /**
     * Main frequency form — queried fresh every time (same reason as buybox).
     * Returns the first wlsn-product-form-frequency NOT inside `this`, because
     * the popup also contains one and blocks can be in any order.
     * @returns {HTMLElement|null}
     */
    get frequencyForm() {
      const all = document.querySelectorAll(this.classes.frequencyForm);
      for (const el of all) {
        if (!this.contains(el)) return el;
      }
      return null;
    }

    connectedCallback() {
      setTimeout(() => {
        this._init();
      }, 0);
    }

    disconnectedCallback() {
      this._cleanup();
    }

    _init() {
      this._setupIntersectionObservers();
      this._bindEvents();
      this._initState();
      this._renderLabel();
    }

    /**
     * Set up IntersectionObservers for hide-condition elements.
     * Safe to call multiple times — disconnects existing observers first.
     * @returns {void}
     */
    _setupIntersectionObservers() {
      Object.values(this.observers).forEach((obs) => obs?.disconnect());
      this.observers = {};
      this.visibilityStates = {};

      this.hideConditionSelectors.forEach((condition, index) => {
        const element = document.querySelector(condition.selector);
        if (!element) {
          console.warn(`[WLSNStickyBuybox] Could not find element for ${condition.description} (${condition.selector})`);
          return;
        }

        this.visibilityStates[index] = false;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.visibilityStates[index] = entry.intersectionRatio > 0.2;
              this._updateStickyState();
            });
          },
          {
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
          }
        );

        observer.observe(element);
        this.observers[index] = observer;
      });
    }

    /**
     * Bind event listeners.
     * @returns {void}
     */
    _bindEvents() {
      window.addEventListener('resize', this.boundHandleResize, { passive: true });
      window.addEventListener('wlsn:purchase-type:change', this.boundHandlePurchaseTypeChange);
      window.addEventListener('wlsn:selector:change', this.boundHandleSellingPlanChange);
      window.addEventListener('wlsn:buybox:product-variant-change', this.boundHandleProductVariantChange);
      window.addEventListener('wlsn:page:updated', this.boundHandlePageUpdated);
      window.addEventListener('wlsn:buybox:frequency-price:update', this.boundHandleFrequencyPriceUpdate);
      document.addEventListener('click', this.boundHandleDocumentClick);

      const { selectorButton, popup } = this.elements;
      if (selectorButton) {
        selectorButton.addEventListener('click', this.boundHandleSelectorButtonClick);
      }
      if (popup) {
        popup.addEventListener('change', this.boundHandlePopupChange);
      }
    }

    /**
     * Unbind event listeners.
     * @returns {void}
     */
    _unbindEvents() {
      window.removeEventListener('resize', this.boundHandleResize);
      window.removeEventListener('wlsn:purchase-type:change', this.boundHandlePurchaseTypeChange);
      window.removeEventListener('wlsn:selector:change', this.boundHandleSellingPlanChange);
      window.removeEventListener('wlsn:buybox:product-variant-change', this.boundHandleProductVariantChange);
      window.removeEventListener('wlsn:page:updated', this.boundHandlePageUpdated);
      window.removeEventListener('wlsn:buybox:frequency-price:update', this.boundHandleFrequencyPriceUpdate);
      document.removeEventListener('click', this.boundHandleDocumentClick);

      const { selectorButton, popup } = this.elements;
      if (selectorButton) {
        selectorButton.removeEventListener('click', this.boundHandleSelectorButtonClick);
      }
      if (popup) {
        popup.removeEventListener('change', this.boundHandlePopupChange);
      }
    }

    // ─── State ───────────────────────────────────────────────────────────────────

    /**
     * Read the initial state from the DOM once on startup.
     * Quantity comes from the main buybox; frequency comes from the popup's
     * frequency form which is Liquid-rendered with the correct initial values
     * (checked attribute + selected class on the first plan item).
     * @returns {void}
     */
    _initState() {
      // Quantity — from main buybox
      const buybox = this.buybox;
      if (buybox) {
        const checkedQV = buybox.querySelector(`.${this.classes.quantityInput}:checked`);
        if (checkedQV) {
          this.currentState.quantityLabel = checkedQV
            .closest(`.${this.classes.quantityItem}`)
            ?.querySelector(`.${this.classes.quantityLabel}`)
            ?.textContent?.trim() || '';
        }
      }

      // Frequency — from popup (always inside `this`, Liquid-initialized correctly)
      const { popup } = this.elements;
      if (popup) {
        const popupFrequency = popup.querySelector(this.classes.frequencyForm);
        if (popupFrequency) {
          const checkedType = popupFrequency.querySelector(this.classes.inputPurchaseTypeChecked);
          this._setFrequencyStateForType(checkedType?.value, popupFrequency);
        }
      }
    }

    /**
     * Update currentState.frequencyLabel based on the given purchase type.
     * Reads the plan name or OTP label directly from the popup's frequency form,
     * which is always accessible and already updated before this is called.
     * @param {string} purchaseType - 'subscription' | 'one-time'
     * @param {HTMLElement} [popupFrequency] - popup's wlsn-product-form-frequency element
     * @returns {void}
     */
    _setFrequencyStateForType(purchaseType, popupFrequency) {
      if (!popupFrequency) {
        const { popup } = this.elements;
        popupFrequency = popup?.querySelector(this.classes.frequencyForm);
      }
      if (!popupFrequency) return;

      if (purchaseType === this.purchaseType.SUB) {
        this.currentState.frequencyLabel = this._getSellingPlanLabel(popupFrequency);
      } else if (purchaseType === this.purchaseType.OTP) {
        this.currentState.frequencyLabel =
          popupFrequency.querySelector(`.${this.classes.otpLabel}`)?.textContent?.trim() || '';
      } else {
        this.currentState.frequencyLabel = '';
      }
    }

    /**
     * Render the selector button label from currentState.
     * Pure render — no DOM reads, no side effects.
     * @returns {void}
     */
    _renderLabel() {
      const { selectorLabel } = this.elements;
      if (!selectorLabel) return;

      const parts = [];
      if (this.currentState.quantityLabel) parts.push(this.currentState.quantityLabel);
      if (this.currentState.frequencyLabel) parts.push(this.currentState.frequencyLabel);

      selectorLabel.textContent = parts.join(' | ');
    }

    // ─── Event Handlers ─────────────────────────────────────────────────────────

    _handleResize() {
      this._updateStickyState();
    }

    _handleSelectorButtonClick(event) {
      event.stopPropagation();
      this._togglePopup();
    }

    _handleDocumentClick(event) {
      const { popup } = this.elements;
      if (!popup || popup.hidden) return;
      if (!this.contains(event.target)) {
        this._closePopup();
      }
    }

    /**
     * Handle input changes within the popup.
     * Updates currentState immediately from the event (no DOM re-read), then syncs
     * the selection to the main form.
     * @param {Event} event
     * @returns {void}
     */
    _handlePopupChange(event) {
      const input = event.target;

      if (input.matches(`.${this.classes.quantityInput}`)) {
        // wlsn-product-variants dispatches wlsn:buybox:product-variant-change,
        // which we listen to globally for state updates — no manual sync needed here.
        return;
      }

      if (input.matches('input[name="purchase-type"]')) {
        // Update state immediately — the popup's wlsn-product-form-frequency
        // handlePurchaseTypeChange has already run at the input level (before bubbling
        // to this popup div), so the popup DOM is already in the correct state.
        this._setFrequencyStateForType(input.value);
        this._syncPurchaseTypeToMainForm(input.value);
        this._renderLabel();
        return;
      }

      if (input.matches('input[name="selling_plan"], input[name="selling_plan_value"]')) {
        // wlsn-options-selector._selectValue already updated the `selected` class
        // before dispatching this change event, so the popup's selected item is correct.
        const { popup } = this.elements;
        const popupFrequency = popup?.querySelector(this.classes.frequencyForm);
        if (popupFrequency) {
          this.currentState.frequencyLabel = this._getSellingPlanLabel(popupFrequency);
        }
        this._syncSellingPlanToMainForm(input.value);
        this._renderLabel();
      }
    }

    /**
     * Handle purchase type change event fired by the main form.
     * Updates state, syncs the popup UI, and renders the label.
     * @param {CustomEvent} event
     * @returns {void}
     */
    _handlePurchaseTypeChange(event) {
      if (this.isSyncing) return;
      const { purchaseType } = event.detail;
      const { popup } = this.elements;

      // Sync popup radio so its UI matches the main form
      if (popup) {
        const popupInput = popup.querySelector(`input[name="purchase-type"][value="${purchaseType}"]`);
        if (popupInput && !popupInput.checked) {
          this.isSyncing = true;
          popupInput.checked = true;
          popupInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.isSyncing = false;
        }
      }

      // Update state — popup is now synced, so its DOM reflects the new type
      this._setFrequencyStateForType(purchaseType);
      this._renderLabel();
    }

    /**
     * Handle product variant change (quantity selection).
     * Re-reads the quantity label from the main buybox (authoritative source).
     * @param {CustomEvent} event
     * @returns {void}
     */
    _handleProductVariantChange() {
      const buybox = this.buybox;
      if (buybox) {
        const checkedQV = buybox.querySelector(`.${this.classes.quantityInput}:checked`);
        if (checkedQV) {
          this.currentState.quantityLabel = checkedQV
            .closest(`.${this.classes.quantityItem}`)
            ?.querySelector(`.${this.classes.quantityLabel}`)
            ?.textContent?.trim() || '';
        }
      }
      this._renderLabel();
    }

    /**
     * Handle selling plan change from wlsn-options-selector.
     * Gets the plan name directly from the event's selectedItem — no DOM traversal.
     * Guarded by isSyncing to avoid double-updating during popup→main syncs.
     * @param {CustomEvent} event
     * @returns {void}
     */
    _handleSellingPlanChange(event) {
      if (this.isSyncing) return;
      const selectedItem = event.detail?.selectedItem;
      if (selectedItem) {
        const span = selectedItem.querySelector(
          '.wlsn-product-form-frequency__options-selector-content span'
        );
        if (span) {
          this.currentState.frequencyLabel = span.textContent.trim();
          this._renderLabel();
        }
      }
    }

    /**
     * Handle frequency price update from the main wlsn-product-form.
     * Updates the price elements inside the sticky popup's isolated frequency form,
     * which lives outside wlsn-product-form and is therefore not updated directly.
     * @param {CustomEvent} event
     * @returns {void}
     */
    _handleFrequencyPriceUpdate(event) {
      const { price, compareAtPrice } = event.detail;
      const { popup } = this.elements;
      if (!popup) return;

      const priceEl = popup.querySelector('.js-frequency-price');
      const compareAtEl = popup.querySelector('.js-frequency-compare-at-price');

      if (priceEl) priceEl.textContent = price;
      if (compareAtEl) compareAtEl.textContent = compareAtPrice;
    }

    /**
     * Handle Shopify AJAX section re-render.
     * Re-observes hide-condition elements and re-initializes state from the DOM.
     * @returns {void}
     */
    _handlePageUpdated() {
      this._setupIntersectionObservers();
      this._initState();
      this._renderLabel();
    }

    // ─── Popup ───────────────────────────────────────────────────────────────────

    _togglePopup() {
      const { popup } = this.elements;
      if (!popup) return;
      popup.hidden ? this._openPopup() : this._closePopup();
    }

    /**
     * Open the popup and sync its inputs to current main form state.
     * @returns {void}
     */
    _openPopup() {
      const { popup, selectorButton } = this.elements;
      if (!popup) return;
      this._syncPopupFromMainForm();
      popup.hidden = false;
      selectorButton?.setAttribute('aria-expanded', 'true');
      selectorButton?.classList.add(this.classes.isOpen);
    }

    _closePopup() {
      const { popup, selectorButton } = this.elements;
      if (!popup) return;
      popup.hidden = true;
      selectorButton?.setAttribute('aria-expanded', 'false');
      selectorButton?.classList.remove(this.classes.isOpen);
    }

    /**
     * Sync all popup inputs to reflect the current main form state.
     * Called every time the popup opens to guarantee it shows the correct selections.
     * isSyncing is set for the entire block so that dispatched change events do not
     * propagate back and trigger syncs to the main form.
     * @returns {void}
     */
    _syncPopupFromMainForm() {
      const { popup } = this.elements;
      if (!popup) return;

      this.isSyncing = true;

      // Sync quantity radio
      const buybox = this.buybox;
      if (buybox) {
        const checkedQV = buybox.querySelector(`.${this.classes.quantityInput}:checked`);
        if (checkedQV) {
          const popupQV = popup.querySelector(`.${this.classes.quantityInput}[value="${checkedQV.value}"]`);
          if (popupQV && !popupQV.checked) {
            popupQV.checked = true;
          }
        }
      }

      const frequencyForm = this.frequencyForm;
      if (frequencyForm) {
        // Sync options selector selected item BEFORE dispatching purchase-type change.
        // wlsn-product-form-frequency.setSellingPlanInput() reads the `selected` class
        // on wlsn-options-selector-item to determine the selling plan value, so the
        // correct item must be marked before the component processes the change event.
        const mainSellingPlan = frequencyForm.querySelector('input[name="selling_plan"]');
        const popupOptionsSelector = popup.querySelector('wlsn-options-selector');
        // Only sync the selected item when subscription is active (selling_plan has a value).
        // When OTP is active the main form's selling_plan is '' — toggling with an empty
        // targetValue would clear `selected` from all items, destroying the popup's
        // memory of which plan was last selected.
        if (mainSellingPlan && mainSellingPlan.value && popupOptionsSelector) {
          const targetValue = mainSellingPlan.value;
          popupOptionsSelector.querySelectorAll('wlsn-options-selector-item').forEach((item) => {
            item.classList.toggle('selected', item.getAttribute('data-option-value') === targetValue);
          });
        }

        // Sync purchase-type radio and dispatch change so the popup's
        // wlsn-product-form-frequency component updates its active CSS classes
        // and selling plan input value via setSellingPlanInput().
        const checkedType = frequencyForm.querySelector(this.classes.inputPurchaseTypeChecked);
        if (checkedType) {
          const popupType = popup.querySelector(`input[name="purchase-type"][value="${checkedType.value}"]`);
          if (popupType) {
            popupType.checked = true;
            popupType.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      this.isSyncing = false;
    }

    // ─── Label Helpers ───────────────────────────────────────────────────────────

    /**
     * Read the current selling plan display name from a frequency form container.
     * Handles both single-plan and multi-plan (options selector) layouts.
     * @param {HTMLElement} container
     * @returns {string}
     */
    _getSellingPlanLabel(container) {
      // Single plan layout
      const singlePlanEl = container.querySelector(`.${this.classes.planNameSingle}`);
      if (singlePlanEl) {
        return singlePlanEl.textContent.replace(/[()]/g, '').trim();
      }
      // Multi plan — selected item is set in Liquid HTML and kept in sync by the component
      const selectedItem = container.querySelector('wlsn-options-selector-item.selected');
      if (selectedItem) {
        const span = selectedItem.querySelector(
          '.wlsn-product-form-frequency__options-selector-content span'
        );
        if (span) return span.textContent.trim();
      }
      return '';
    }

    // ─── Sync to Main Form ───────────────────────────────────────────────────────

    /**
     * Sync popup purchase type selection to the main frequency form.
     * @param {string} purchaseType
     * @returns {void}
     */
    _syncPurchaseTypeToMainForm(purchaseType) {
      if (this.isSyncing) return;
      this.isSyncing = true;

      const frequencyForm = this.frequencyForm;
      if (frequencyForm) {
        const targetInput = frequencyForm.querySelector(`input[name="purchase-type"][value="${purchaseType}"]`);
        if (targetInput && !targetInput.checked) {
          targetInput.checked = true;
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.isSyncing = false;
    }

    /**
     * Sync popup selling plan selection to the main frequency form.
     * @param {string} sellingPlanId
     * @returns {void}
     */
    _syncSellingPlanToMainForm(sellingPlanId) {
      if (this.isSyncing) return;
      this.isSyncing = true;

      const frequencyForm = this.frequencyForm;
      if (frequencyForm) {
        const mainInput = frequencyForm.querySelector('input[name="selling_plan"]');
        if (mainInput && mainInput.value !== sellingPlanId) {
          mainInput.value = sellingPlanId;
          mainInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.isSyncing = false;
    }

    // ─── Sticky Visibility ───────────────────────────────────────────────────────

    _updateStickyState() {
      const anyHideConditionVisible = Object.values(this.visibilityStates).some((state) => state === true);
      if (!anyHideConditionVisible) {
        this._showSticky();
      } else {
        this._hideSticky();
        this._closePopup();
      }
    }

    _showSticky() {
      if (!this.classList.contains(this.classes.active)) {
        this.classList.add(this.classes.active);
      }
    }

    _hideSticky() {
      if (!this.classList.contains(this.classes.active)) return;
      this.classList.remove(this.classes.active);
    }

    // ─── Lifecycle ───────────────────────────────────────────────────────────────

    _cleanup() {
      Object.values(this.observers).forEach((observer) => observer?.disconnect());
      this._unbindEvents();
      this._internalElementsCache = null;
    }
  }

  customElements.define('wlsn-buybox-sticky', WLSNStickyBuybox);
}
